import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import {
  useCreateNumberRequest,
  useTransitionNumberRequest,
  useUploadNumberRequestDocument,
} from "@/features/number-requests/hooks/use-number-requests";
import {
  NUMBER_REQUEST_CREATION_KINDS,
  type AvailableNumber,
  type CreateNumberRequestData,
  type NumberRequestKind,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  BuildingsIcon,
  PhonePlusIcon,
  PlugsConnectedIcon,
  PlusIcon,
  UserIcon,
} from "@phosphor-icons/react";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChoiceCard } from "./create/choice-card";
import { type CompanyAnswer, CompanyStep } from "./create/company-step";
import { type NumberOffer, NumberStep } from "./create/number-step";

type Holder = "company" | "individual";
type Step = "holder" | "kind" | "company" | "number" | "sip";

const KIND_ICONS: Partial<Record<NumberRequestKind, typeof PhonePlusIcon>> = {
  new_number: PhonePlusIcon,
  sip_trunk: PlugsConnectedIcon,
};

/** The steps a path walks through, in order — which is also the "2/4". */
const pathOf = (holder: Holder, kind: NumberRequestKind): Step[] =>
  holder === "individual"
    ? ["holder", "number"]
    : ["holder", "kind", "company", kind === "sip_trunk" ? "sip" : "number"];

/**
 * Opening a number request is the whole of the client's part of it.
 *
 * It used to be one question (which kind) and then a page of blocks to fill
 * in and papers to upload. Now the dialog asks everything, in the order it
 * decides things: who the line is for (a company or a private person), then —
 * for a company — a new number or a SIP access, who the company is (its SIREN,
 * checked against the French directory, or a registration document when it is
 * not French), and finally the number itself, picked from what the carrier
 * has in the company's region (a mobile 06/07 for a private person). What is
 * left afterwards is ours, so the page it lands on only shows where things
 * stand.
 *
 * The request is created draft, the document attached, then submitted, so a
 * failure half-way leaves a request the page can finish rather than nothing.
 */
export const CreateNumberRequestDialog = () => {
  const { t } = useTranslation("numberRequests");
  const { company: clientId } = useParams<{ company: string }>();
  const navigate = useNavigate();
  const currentClient = useCurrentClient();
  const createRequest = useCreateNumberRequest(clientId || "");
  const upload = useUploadNumberRequestDocument(clientId || "");
  const transition = useTransitionNumberRequest(clientId || "");

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("holder");
  const [holder, setHolder] = useState<Holder>("company");
  const [kind, setKind] = useState<NumberRequestKind>("new_number");
  const [company, setCompany] = useState<CompanyAnswer | null>(null);
  const [prefix, setPrefix] = useState<string | undefined>(undefined);
  const [number, setNumber] = useState<AvailableNumber | null>(null);
  const [offer, setOffer] = useState<NumberOffer>("loading");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const country = (currentClient?.client.default_country || "FR").toUpperCase();
  const clientAddress = currentClient?.client.address || "";

  const reset = () => {
    setStep("holder");
    setHolder("company");
    setKind("new_number");
    setCompany(null);
    setPrefix(undefined);
    setNumber(null);
    setOffer("loading");
    setNotes("");
  };

  const path = pathOf(holder, kind);
  const index = path.indexOf(step);
  const isLast = index === path.length - 1;

  const onPrefixChange = useCallback((value: string) => setPrefix(value), []);
  const onSelect = useCallback(
    (value: AvailableNumber | null) => setNumber(value),
    [],
  );

  const canContinue = (() => {
    if (step === "company") return company !== null;
    // With numbers on offer, one is picked; with none, the prefix is the
    // request ("a number starting with 04") and we attribute one.
    if (step === "number") return Boolean(number) || offer === "none";
    return true;
  })();

  const back = () => {
    if (index > 0) setStep(path[index - 1]);
  };

  const next = () => {
    if (!isLast) {
      // The region the numbers are searched in follows the company: a new
      // company means a new suggestion.
      if (step === "company") {
        setPrefix(undefined);
        setNumber(null);
      }
      setStep(path[index + 1]);
      return;
    }
    void submit();
  };

  const submit = async () => {
    const data: CreateNumberRequestData = {
      kind: holder === "individual" ? "new_number" : kind,
      holder_type: holder,
      country,
      ...(company?.mode === "siren"
        ? { company_siren: company.company.siren }
        : company?.mode === "foreign"
          ? { company_name: company.name, company_check: "foreign" }
          : {}),
      ...(step === "number"
        ? {
            phone_number: number?.phone_number || "",
            desired_prefix: prefix || "",
          }
        : {}),
      ...(step === "sip" ? { notes: notes.trim() } : {}),
    };

    setIsSubmitting(true);
    let requestId: string | null = null;
    try {
      const request = await createRequest.mutateAsync(data);
      requestId = request.id;

      if (company?.mode === "foreign") {
        await upload.mutateAsync({
          requestId,
          type: "company_registration",
          file: company.file,
        });
      }
      await transition.mutateAsync({ requestId, state: "documents_review" });

      toast.success(t("numberRequests.messages.created"));
    } catch (error) {
      // Created but not sent: the page it lands on can finish it.
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.createFailed"),
      );
    } finally {
      setIsSubmitting(false);
    }

    if (!requestId) return;
    setOpen(false);
    reset();
    navigate(`/${clientId}/settings/connectors/number-requests/${requestId}`);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
        if (!value) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <PlusIcon className="h-4 w-4" />
          {t("numberRequests.create.button")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <p className="text-muted-foreground text-xs tabular-nums">
            {t("numberRequests.create.stepOf", {
              current: index + 1,
              total: path.length,
            })}
          </p>
          <DialogTitle>{t(`numberRequests.create.${step}.title`)}</DialogTitle>
          <DialogDescription>
            {t(`numberRequests.create.${step}.description`)}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {step === "holder" && (
            <div className="space-y-3">
              <ChoiceCard
                icon={BuildingsIcon}
                title={t("numberRequests.create.holder.company")}
                description={t("numberRequests.create.holder.companyHelp")}
                selected={holder === "company"}
                onClick={() => setHolder("company")}
              />
              <ChoiceCard
                icon={UserIcon}
                title={t("numberRequests.create.holder.individual")}
                description={t("numberRequests.create.holder.individualHelp")}
                selected={holder === "individual"}
                onClick={() => setHolder("individual")}
              />
            </div>
          )}

          {step === "kind" && (
            <div className="space-y-3">
              {NUMBER_REQUEST_CREATION_KINDS.map((option) => (
                <ChoiceCard
                  key={option}
                  icon={KIND_ICONS[option] || PhonePlusIcon}
                  title={t(`numberRequests.kinds.${option}`)}
                  description={t(`numberRequests.kindSwitch.${option}`)}
                  selected={kind === option}
                  onClick={() => setKind(option)}
                />
              ))}
            </div>
          )}

          {step === "company" && (
            <CompanyStep value={company} onChange={setCompany} />
          )}

          {step === "number" && (
            <NumberStep
              type={holder === "individual" ? "mobile" : "local"}
              country={country}
              hints={
                company?.mode === "siren"
                  ? {
                      postcode: company.company.postcode,
                      address: company.company.address || clientAddress,
                    }
                  : { address: clientAddress }
              }
              prefix={prefix}
              onPrefixChange={onPrefixChange}
              selected={number}
              onSelect={onSelect}
              onOfferChange={setOffer}
            />
          )}

          {step === "sip" && (
            <div className="space-y-2">
              <Label htmlFor="sip-notes">
                {t("numberRequests.create.sip.notesLabel")}
              </Label>
              <Textarea
                id="sip-notes"
                rows={5}
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
                placeholder={t("numberRequests.create.sip.notesPlaceholder")}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          {index > 0 ? (
            <Button
              type="button"
              variant="outline"
              onClick={back}
              disabled={isSubmitting}
            >
              {t("numberRequests.create.back")}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {t("numberRequests.create.cancel")}
            </Button>
          )}
          <Button
            type="button"
            onClick={next}
            disabled={!canContinue || isSubmitting}
          >
            {isSubmitting
              ? t("numberRequests.create.creating")
              : isLast
                ? step === "number" && !number && offer === "none"
                  ? t("numberRequests.create.submitWithoutNumber")
                  : t("numberRequests.create.send")
                : t("numberRequests.create.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
